import React from 'react';
import { NavLink } from 'react-router-dom';
import { 
  Home, 
  CreditCard, 
  PieChart, 
  BarChart3, 
  Settings,
  Plus,
  Tag,
  X
} from 'lucide-react';
import { cn } from '../../utils/cn';
import useBudgetStore from '../../store/budgetStore';
import useExpenseStore from '../../store/expenseStore';
import { formatCurrency } from '../../utils/formatters';

const navigation = [
  { name: 'Dashboard', href: '/', icon: Home },
  { name: 'Expenses', href: '/expenses', icon: CreditCard },
  { name: 'Budget', href: '/budget', icon: PieChart },
  { name: 'Analytics', href: '/analytics', icon: BarChart3 },
  { name: 'Tags', href: '/analytics/tags', icon: Tag },
  { name: 'Settings', href: '/settings', icon: Settings },
];

const Sidebar = ({ onClose }) => {
  return (
    <div className="flex flex-col flex-grow bg-white border-r border-gray-200">
      {/* Logo */}
      <div className="flex items-center justify-between flex-shrink-0 px-4 py-6">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-primary rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">F</span>
            </div>
          </div>
          <div className="ml-3">
            <h1 className="text-lg font-semibold text-gray-900">Family Finance</h1>
          </div>
        </div>
        {/* Mobile close button */}
        {onClose && (
          <button
            onClick={onClose}
            className="lg:hidden p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
          >
            <X className="h-6 w-6" />
          </button>
        )}
      </div>

      {/* Quick Add Button */}
      <div className="px-4 mb-6">
        <NavLink
          to="/expenses/add"
          className="w-full flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Expense
        </NavLink>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-1">
        {navigation.map((item) => (
          <NavLink
            key={item.name}
            to={item.href}
            end={item.href === '/analytics'} // Use exact matching for Analytics
            className={({ isActive }) =>
              cn(
                'group flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
                isActive
                  ? 'bg-primary text-white'
                  : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
              )
            }
          >
            {({ isActive }) => (
              <>
                <item.icon
                  className={cn(
                    'mr-3 flex-shrink-0 h-5 w-5',
                    isActive ? 'text-white' : 'text-gray-400 group-hover:text-gray-500'
                  )}
                />
                {item.name}
              </>
            )}
          </NavLink>
        ))}
      </nav>

      {/* Bottom section - Budget Summary */}
      <BudgetSummary />
    </div>
  );
};

// Budget Summary Component
const BudgetSummary = () => {
  const { currentBudget, getBudgetProgress } = useBudgetStore();
  const { getCurrentMonthExpenses, getTotalExpenses } = useExpenseStore();
  
  const currentMonthExpenses = getCurrentMonthExpenses();
  const totalSpent = getTotalExpenses(currentMonthExpenses);
  
  if (!currentBudget) {
    return (
      <div className="flex-shrink-0 p-4">
        <div className="bg-gray-50 rounded-lg p-4">
          <h3 className="text-sm font-medium text-gray-900 mb-2">
            Monthly Budget
          </h3>
          <p className="text-xs text-gray-500">No budget set for this month</p>
        </div>
      </div>
    );
  }

  const progress = getBudgetProgress(currentBudget);
  
  return (
    <div className="flex-shrink-0 p-4">
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-sm font-medium text-gray-900 mb-2">
          Monthly Budget
        </h3>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Spent</span>
            <span className="font-medium">{formatCurrency(totalSpent)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Budget</span>
            <span className="font-medium">{formatCurrency(currentBudget.totalBudget)}</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className={`h-2 rounded-full transition-all duration-300 ${
                progress.percentage > 90 ? 'bg-red-500' : 
                progress.percentage > 75 ? 'bg-yellow-500' : 'bg-primary'
              }`}
              style={{ width: `${Math.min(progress.percentage, 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-500">
            {progress.percentage.toFixed(0)}% of budget used
          </p>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;
